import {
  isProjectScopedSettingKey,
  setProjectSettingOverrides,
  clearProjectSettingOverrides,
  type ProjectScopedServerSettingKey,
} from "@afar-research/afar";
import type {
  EnvironmentId,
  ProjectSettingsOverrides,
  ServerSettings,
  ServerSettingsPatch,
} from "@afar-research/contracts";

import type { ResolvedSettingsScope } from "./settingsScope";
import { scopedProjectIdsByEnvironment } from "./settingsScope";

export interface SettingsWrite {
  readonly environmentId: EnvironmentId;
  readonly patch: ServerSettingsPatch;
}

export interface SettingsWritePlan {
  readonly writes: readonly SettingsWrite[];
  /** Why nothing will be written, for the caller to surface instead of failing silently. */
  readonly refusal: string | null;
}

const EMPTY_PLAN: SettingsWritePlan = { writes: [], refusal: null };

/**
 * Turn one edit into the writes it actually implies.
 *
 * An Environment scope writes the patch to each selected Environment. A
 * project scope writes the project-scopable keys into each selected AFAR
 * Project's override entry on its own Environment, and refuses the rest:
 * a key that is not project-scopable has no per-project answer to store, and
 * silently widening it to the Environment would change far more than the row
 * the user was looking at.
 */
export function planSettingsWrite(input: {
  readonly scope: ResolvedSettingsScope;
  readonly patch: ServerSettingsPatch;
  /** Current settings per Environment, so an override folds onto what is there. */
  readonly settingsByEnvironment: ReadonlyMap<EnvironmentId, ServerSettings>;
}): SettingsWritePlan {
  const { scope, patch, settingsByEnvironment } = input;
  const keys = Object.keys(patch);
  if (keys.length === 0) return EMPTY_PLAN;

  if (scope.kind === "unavailable") {
    return { writes: [], refusal: scope.message };
  }

  if (scope.kind !== "project") {
    return {
      writes: scope.environmentIds.map((environmentId) => ({ environmentId, patch })),
      refusal: null,
    };
  }

  const unsupported = keys.filter((key) => !isProjectScopedSettingKey(key));
  if (unsupported.length > 0) {
    return {
      writes: [],
      refusal: `${unsupported.join(", ")} ${unsupported.length === 1 ? "is" : "are"} set for the whole environment. Choose the environment to change it.`,
    };
  }

  const values = patch as Partial<ProjectSettingsOverrides>;
  const writes: SettingsWrite[] = [];
  for (const [environmentId, projectIds] of scopedProjectIdsByEnvironment(scope)) {
    const settings = settingsByEnvironment.get(environmentId);
    if (settings === undefined) continue;
    const overrides = Object.fromEntries(
      projectIds.map((projectId) => [
        projectId,
        setProjectSettingOverrides(settings, projectId, values),
      ]),
    );
    writes.push({ environmentId, patch: { projectSettingsOverrides: overrides } });
  }
  return { writes, refusal: null };
}

/** Drop `keys` from every project in scope so they inherit their Environment again. */
export function planSettingsOverrideClear(input: {
  readonly scope: ResolvedSettingsScope;
  readonly keys: readonly ProjectScopedServerSettingKey[];
  readonly settingsByEnvironment: ReadonlyMap<EnvironmentId, ServerSettings>;
}): SettingsWritePlan {
  const { scope, keys, settingsByEnvironment } = input;
  if (keys.length === 0 || scope.kind !== "project") return EMPTY_PLAN;
  const writes: SettingsWrite[] = [];
  for (const [environmentId, projectIds] of scopedProjectIdsByEnvironment(scope)) {
    const settings = settingsByEnvironment.get(environmentId);
    if (settings === undefined) continue;
    const overrides = Object.fromEntries(
      projectIds.map((projectId) => [
        projectId,
        clearProjectSettingOverrides(settings, projectId, keys),
      ]),
    );
    writes.push({ environmentId, patch: { projectSettingsOverrides: overrides } });
  }
  return { writes, refusal: null };
}

export interface SettingsWriteOutcome {
  readonly savedEnvironmentIds: readonly EnvironmentId[];
  readonly failedEnvironmentIds: readonly EnvironmentId[];
}

/**
 * Run every write before reporting. One Environment failing must not hide the
 * ones that succeeded, or the user cannot tell which machines now disagree.
 */
export async function runSettingsWritePlan(
  plan: SettingsWritePlan,
  write: (input: SettingsWrite) => Promise<boolean>,
): Promise<SettingsWriteOutcome> {
  const results = await Promise.all(
    plan.writes.map(async (entry) => ({
      environmentId: entry.environmentId,
      ok: await write(entry).catch(() => false),
    })),
  );
  return {
    savedEnvironmentIds: results.flatMap((r) => (r.ok ? [r.environmentId] : [])),
    failedEnvironmentIds: results.flatMap((r) => (r.ok ? [] : [r.environmentId])),
  };
}
